// ─────────────────────────────────────────────────────────────────────────────
// DAGGPT Website Builder - Frontend Service
// SSE stream parser + project save/load
// ─────────────────────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';

// Reads the logged-in user's Supabase ID from localStorage (set by AuthContext)
const getUserId = () => {
  try {
    const daggptUserRaw = localStorage.getItem('daggpt_user');
    if (daggptUserRaw) {
      const parsed = JSON.parse(daggptUserRaw);
      if (parsed?.id) return parsed.id;
    }
    const raw = localStorage.getItem('supabase.auth.token') ||
                localStorage.getItem('sb-auth-token') ||
                Object.entries(localStorage).find(([k]) => k.includes('auth-token'))?.[1];
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return parsed?.user?.id || parsed?.currentSession?.user?.id || '';
  } catch { return ''; }
};

/**
 * Parse AI response containing <FILE:path>content</FILE> blocks.
 * Returns a Map of { path: content }
 */
export function parseFilesFromResponse(text) {
  const files = {};
  const regex = /<FILE:(.*?)>\n?([\s\S]*?)\n?<\/FILE>/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const path = match[1].trim();
    const content = match[2];
    files[path] = content;
  }
  return files;
}

/**
 * Stream a full project generation from the builder API.
 * Calls onChunk(text) with raw text fragments, and onDone(fullText) on completion.
 */
export async function generateProject({ prompt, framework, model, conversationHistory = [], onChunk, onDone, onError }) {
  try {
    const response = await fetch(`${API_URL}/api/website-builder/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-user-id': getUserId() },
      body: JSON.stringify({ prompt, framework, model, conversationHistory }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Generation failed' }));
      throw new Error(err.error || `HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep incomplete line in buffer

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const jsonStr = line.slice(6);
        try {
          const event = JSON.parse(jsonStr);
          if (event.type === 'content') {
            fullText += event.content;
            if (onChunk) onChunk(event.content, fullText);
          } else if (event.type === 'error') {
            if (onError) onError(event.error);
            return;
          } else if (event.type === 'done') {
            // done
          }
        } catch (e) {
          // skip malformed JSON
        }
      }
    }

    if (onDone) onDone(fullText);
    return fullText;
  } catch (err) {
    if (onError) onError(err.message);
    throw err;
  }
}

/**
 * Stream a patch request for iterative edits.
 */
export async function patchProject({ instruction, files, model, conversationHistory = [], onChunk, onDone, onError }) {
  try {
    const response = await fetch(`${API_URL}/api/website-builder/patch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-user-id': getUserId() },
      body: JSON.stringify({ instruction, files, model, conversationHistory }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Patch failed' }));
      throw new Error(err.error || `HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'content') {
            fullText += event.content;
            if (onChunk) onChunk(event.content, fullText);
          } else if (event.type === 'error') {
            if (onError) onError(event.error);
            return;
          }
        } catch (e) {}
      }
    }

    if (onDone) onDone(fullText);
    return fullText;
  } catch (err) {
    if (onError) onError(err.message);
    throw err;
  }
}

/**
 * Detect framework from files map.
 */
export function detectFramework(files) {
  const paths = Object.keys(files);
  const pkg = files['package.json'];

  if (pkg) {
    try {
      const parsed = JSON.parse(pkg);
      const deps = { ...parsed.dependencies, ...parsed.devDependencies };
      if (deps['next']) return 'nextjs';
      if (deps['react']) return 'react';
      if (deps['vue']) return 'vue';
      if (deps['svelte']) return 'svelte';
      if (deps['astro']) return 'astro';
    } catch (e) {}
  }

  if (paths.some(p => p.endsWith('.html'))) return 'html';
  return 'unknown';
}

/**
 * Generate a preview HTML by combining files for instant browser rendering.
 * Works for HTML projects (inline CSS/JS) and has fallbacks for React/Next.js.
 */
export function buildPreviewHtml(files) {
  // ─── Case 1: Plain HTML project with index.html ───────────────────────────
  const html = files['index.html'];
  if (html) {
    let result = html;

    // Inline ALL CSS files
    const cssFiles = Object.entries(files).filter(([p]) => p.endsWith('.css'));
    for (const [path, content] of cssFiles) {
      // Try to replace existing <link> references
      const escaped = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const linkRegex = new RegExp(`<link[^>]*href=["'](?:\\.?\\/?)${escaped}["'][^>]*/?>`, 'gi');
      if (linkRegex.test(result)) {
        result = result.replace(linkRegex, `<style>\n${content}\n</style>`);
      } else {
        // Inject before </head>
        result = result.replace('</head>', `<style>\n/* ${path} */\n${content}\n</style>\n</head>`);
      }
    }

    // Inline ALL JS files (not config files)
    const jsFiles = Object.entries(files).filter(([p]) => 
      p.endsWith('.js') && !p.includes('node_modules') && 
      !p.includes('config') && !p.includes('vite') && !p.includes('tailwind')
    );
    for (const [path, content] of jsFiles) {
      const escaped = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const scriptRegex = new RegExp(`<script[^>]*src=["'](?:\\.?\\/?)${escaped}["'][^>]*>\\s*</script>`, 'gi');
      if (scriptRegex.test(result)) {
        result = result.replace(scriptRegex, `<script>\n${content}\n</script>`);
      } else {
        result = result.replace('</body>', `<script>\n/* ${path} */\n${content}\n</script>\n</body>`);
      }
    }

    return result;
  }

  // ─── Case 2: React/Next.js project - extract page component and CSS ───────
  // Find the main page/component file
  const pagePaths = [
    'src/app/page.tsx', 'src/app/page.jsx', 'src/app/page.js',
    'src/pages/index.tsx', 'src/pages/index.jsx', 'src/pages/index.js',
    'src/App.tsx', 'src/App.jsx', 'src/App.js',
    'app/page.tsx', 'app/page.jsx',
  ];
  
  const mainPagePath = pagePaths.find(p => files[p]);
  if (!mainPagePath) return null;

  const pageCode = files[mainPagePath];
  
  // Collect all CSS
  const allCss = Object.entries(files)
    .filter(([p]) => p.endsWith('.css'))
    .map(([p, c]) => `/* ${p} */\n${c}`)
    .join('\n\n');

  // For React/TSX projects, create a standalone HTML preview using CDN React + Babel
  // We strip out imports/exports and render the default export directly
  let cleanedCode = pageCode
    // Remove import statements
    .replace(/^import\s+.*$/gm, '')
    // Remove 'use client' / 'use server' directives
    .replace(/^['"]use (client|server)['"];?\s*$/gm, '')
    // Replace export default function with just function
    .replace(/export\s+default\s+function\s+/g, 'function ')
    // Replace export default with nothing (for arrow functions)
    .replace(/export\s+default\s+/g, 'const __DefaultExport__ = ')
    // Handle "export function" 
    .replace(/export\s+function\s+/g, 'function ')
    // Handle "export const"
    .replace(/export\s+const\s+/g, 'const ')
    // Remove TypeScript type annotations (basic)
    .replace(/:\s*(React\.FC|FC|string|number|boolean|any|void|JSX\.Element|React\.ReactNode)\s*/g, '')
    .replace(/<[A-Z][a-zA-Z]*Props>/g, '')
    // Remove interface/type blocks
    .replace(/^(interface|type)\s+\w+\s*\{[\s\S]*?\}/gm, '')
    .trim();

  // Find the component name (first capitalized function)
  const funcMatch = cleanedCode.match(/function\s+([A-Z][a-zA-Z]*)/);
  const componentName = funcMatch ? funcMatch[1] : null;

  if (!componentName && !cleanedCode.includes('__DefaultExport__')) {
    return buildFallbackPreview(files, allCss);
  }

  const renderTarget = componentName || '__DefaultExport__';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Preview</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet">
  <script src="https://unpkg.com/react@18/umd/react.production.min.js" crossorigin></script>
  <script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js" crossorigin></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', sans-serif; }
    ${allCss}
  </style>
</head>
<body>
  <div id="root"></div>
  <script type="text/babel">
    const { useState, useEffect, useRef, useCallback, useMemo } = React;
    // Stub common Next.js modules
    const Link = ({href, children, ...props}) => React.createElement('a', {href, ...props}, children);
    const Image = ({src, alt, width, height, ...props}) => React.createElement('img', {src, alt, width, height, style:{maxWidth:'100%',height:'auto'}, ...props});
    const useRouter = () => ({push:()=>{},back:()=>{},pathname:'/'});
    const usePathname = () => '/';
    const useSearchParams = () => new URLSearchParams();

    ${cleanedCode}

    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(React.createElement(${renderTarget}));
  </script>
</body>
</html>`;
}

/**
 * Fallback: render file contents as a styled preview page
 */
function buildFallbackPreview(files, css) {
  const fileList = Object.keys(files).map(f => `<li>${f}</li>`).join('');
  return `<!DOCTYPE html>
<html><head>
<meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0">
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Inter',sans-serif; background:linear-gradient(135deg,#f8fafc,#eef2ff); min-height:100vh; display:flex; align-items:center; justify-content:center; padding:2rem; }
  .card { background:white; border-radius:16px; padding:2.5rem; max-width:480px; width:100%; box-shadow:0 4px 24px rgba(0,0,0,0.08); }
  h2 { font-size:1.25rem; color:#1e293b; margin-bottom:0.5rem; }
  p { font-size:0.875rem; color:#64748b; margin-bottom:1.5rem; line-height:1.6; }
  ul { list-style:none; }
  li { font-size:0.8rem; padding:0.5rem 0.75rem; border-radius:8px; background:#f1f5f9; margin-bottom:0.5rem; color:#475569; font-family:monospace; }
  .badge { display:inline-block; font-size:0.7rem; padding:0.25rem 0.75rem; border-radius:99px; background:#e0e7ff; color:#4f46e5; font-weight:600; margin-bottom:1rem; }
  ${css || ''}
</style>
</head><body>
<div class="card">
  <span class="badge">React / Next.js Project</span>
  <h2>📦 Project Generated Successfully</h2>
  <p>This project uses a framework that requires a build step. Click <strong>Export ZIP</strong> to download, then run <code>npm install && npm run dev</code> locally.</p>
  <ul>${fileList}</ul>
</div>
</body></html>`;
}


// ─── Project Persistence (Worker D1 API) ──────────────────────────────────────
const WORKER_URL = 'https://api.daggpt.network';

/**
 * Save project to D1 via the Worker.
 */
export async function saveProject({ id, name, description, framework, files, userId }) {
  try {
    const response = await fetch(`${WORKER_URL}/api/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': userId,
      },
      body: JSON.stringify({ id, name, description, framework, files }),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Save failed');
    return data;
  } catch (err) {
    console.error('[WebsiteBuilder] Save failed:', err);
    throw err;
  }
}

/**
 * Load a project from D1.
 */
export async function loadProject(projectId, userId) {
  try {
    const response = await fetch(`${WORKER_URL}/api/projects/${projectId}`, {
      headers: { 'x-user-id': userId },
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Load failed');
    return data.project;
  } catch (err) {
    console.error('[WebsiteBuilder] Load failed:', err);
    throw err;
  }
}

/**
 * List all projects for a user.
 */
export async function listProjects(userId) {
  try {
    const response = await fetch(`${WORKER_URL}/api/projects`, {
      headers: { 'x-user-id': userId },
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'List failed');
    return data.projects;
  } catch (err) {
    console.error('[WebsiteBuilder] List failed:', err);
    return [];
  }
}

/**
 * Delete a project.
 */
export async function deleteProject(projectId, userId) {
  try {
    const response = await fetch(`${WORKER_URL}/api/projects/${projectId}`, {
      method: 'DELETE',
      headers: { 'x-user-id': userId },
    });
    const data = await response.json();
    return data.success;
  } catch (err) {
    console.error('[WebsiteBuilder] Delete failed:', err);
    return false;
  }
}

/**
 * Create a version snapshot.
 */
export async function createSnapshot(projectId, files, message, userId) {
  try {
    const response = await fetch(`${WORKER_URL}/api/projects/${projectId}/snapshot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': userId,
      },
      body: JSON.stringify({ files, message }),
    });
    const data = await response.json();
    return data;
  } catch (err) {
    console.error('[WebsiteBuilder] Snapshot failed:', err);
    return { success: false };
  }
}
